import React, { useMemo } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

// Canonical vertical aliases (case-insensitive). Mirrors backend
// `VERTICAL_ALIASES` in `routes/talentos.py` so the dropdown collapses
// legacy variants (Beauty / Make Up / Makeup → Beauty & Make Up, Foodies
// → Foodie, Deporte → Sport, Dance → Baile) even when the DB still has
// un-migrated rows.
const VERTICAL_ALIASES = {
  foodies: 'Foodie',
  foodie: 'Foodie',
  deporte: 'Sport',
  sport: 'Sport',
  beauty: 'Beauty & Make Up',
  'make up': 'Beauty & Make Up',
  makeup: 'Beauty & Make Up',
  'beauty & make up': 'Beauty & Make Up',
  dance: 'Baile',
  baile: 'Baile',
};

const canonicalizeVertical = (token) => {
  const key = String(token || '').trim().toLowerCase();
  return VERTICAL_ALIASES[key] || token;
};

/** Splits a talento's `vertical` string into individual values (canonicalized). */
export const parseVerticals = (raw) => {
  if (!raw) return [];
  return String(raw)
    .split(/[,/]/)
    .map((v) => v.trim())
    .filter((v) => v.length > 0)
    .map(canonicalizeVertical);
};

/** True if `talento` matches at least one of the given verticals (case-insensitive).
 *  An empty / null / '__ALL__' filter means "no restriction" and matches everything.
 */
export const talentMatchesAnyVertical = (talento, filters) => {
  const active = (filters || []).filter((f) => f && f !== '__ALL__');
  if (active.length === 0) return true;
  const tags = parseVerticals(talento.vertical).map((v) => v.toLowerCase());
  return active.some((f) => tags.includes(f.toLowerCase()));
};

// Backwards-compat for callers that filter by a single vertical
export const talentMatchesVertical = (talento, vertical) =>
  talentMatchesAnyVertical(talento, [vertical]);

const collectVerticals = (talentos) => {
  const map = new Map();
  talentos.forEach((t) => {
    parseVerticals(t.vertical).forEach((v) => {
      const key = v.toLowerCase();
      const entry = map.get(key) || { label: v, count: 0 };
      entry.count += 1;
      if (v[0] === v[0].toUpperCase()) entry.label = v;
      map.set(key, entry);
    });
  });
  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
};

/** Two combinable dropdowns. Selecting both filters by OR (union).
 *  Uses Radix UI (shadcn) Select — bullet-proof on iOS Safari (unlike native <select>).
 */
const VerticalFilterSelect = ({ talentos, value, onChange, accentColor = '#2A9D8F' }) => {
  const verticals = useMemo(() => collectVerticals(talentos), [talentos]);
  if (verticals.length === 0) return null;

  const [first, second] = value || ['__ALL__', '__ALL__'];

  const setFirst = (v) => onChange([v, second]);
  const setSecond = (v) => onChange([first, v]);

  const triggerStyle = (current) => ({
    fontFamily: "'DM Sans', sans-serif",
    borderColor: current !== '__ALL__' ? accentColor : '#d1d5db',
    color: current !== '__ALL__' ? accentColor : '#374151',
    borderRadius: '9999px',
    padding: '0.5rem 1rem',
    minWidth: 180,
    backgroundColor: 'white',
  });

  return (
    <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center" data-testid="vertical-filter-select">
      <span className="text-sm font-semibold uppercase tracking-wider text-gray-500" style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.1em' }}>
        Filtrar por vertical
      </span>

      <Select value={first} onValueChange={setFirst}>
        <SelectTrigger
          data-testid="vertical-filter-1"
          className="h-auto text-sm font-semibold focus:outline-none"
          style={triggerStyle(first)}
        >
          <SelectValue placeholder="Todos" />
        </SelectTrigger>
        <SelectContent className="max-h-[60vh]">
          <SelectItem value="__ALL__">Todos</SelectItem>
          {verticals.map((v) => (
            <SelectItem key={v.label} value={v.label}>{v.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <span className="text-xs text-gray-400 italic px-1">o también</span>

      <Select value={second} onValueChange={setSecond} disabled={first === '__ALL__'}>
        <SelectTrigger
          data-testid="vertical-filter-2"
          className="h-auto text-sm font-semibold focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          style={triggerStyle(second)}
        >
          <SelectValue placeholder="— Sin segundo filtro —" />
        </SelectTrigger>
        <SelectContent className="max-h-[60vh]">
          <SelectItem value="__ALL__">— Sin segundo filtro —</SelectItem>
          {verticals.filter((v) => v.label !== first).map((v) => (
            <SelectItem key={v.label} value={v.label}>{v.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {(first !== '__ALL__' || second !== '__ALL__') && (
        <button
          type="button"
          onClick={() => onChange(['__ALL__', '__ALL__'])}
          className="text-xs underline text-gray-500 hover:text-gray-800 transition-colors"
          data-testid="vertical-filter-clear"
        >
          Limpiar
        </button>
      )}
    </div>
  );
};

export default VerticalFilterSelect;
